import { useEffect, useState } from "react";
import { ActivityIndicator } from "react-native";
import { useLocalSearchParams } from "expo-router";
import { useUnistyles } from "react-native-unistyles";
import { View, Text, Button } from "@src/components";
import { sizes } from "@src/constants/sizes";
import { useEnvironment } from "@src/config/environment-store";
import { FEATURE_FLAGS } from "@src/services/feature-flags/feature-flags";
import {
  isStorybookPublished,
  openStorybookSession,
  resolveStorybookTarget,
  STORYBOOK_EMAIL_DOMAIN,
  type StorybookSessionResult,
} from "@src/features/storybook/storybook-session";

/**
 * Opens the Storybook site for the signed-in user.
 *
 * The Storybook site has no sign-in of its own: the CloudFront function
 * guarding it bounces anyone without a session here, this screen swaps the
 * user's Vision Auth token for a session cookie, and then sends the browser
 * back to whatever they originally asked for.
 *
 * This deliberately does not live under `/development`. Those routes are gated
 * on the build-time `APP_ENV`, which is always `production` in a deployed web
 * build — the bundle is shared across environments and only the `index.html`
 * meta tags differ — so a screen placed there redirects to `/not-found`
 * everywhere it is actually deployed. The environment is resolved at runtime
 * from those meta tags instead.
 */
export default function StorybookScreen() {
  const { theme } = useUnistyles();
  const params = useLocalSearchParams<{ return?: string | string[] }>();
  const environment = useEnvironment();
  const [result, setResult] = useState<StorybookSessionResult | null>(null);
  const [attempt, setAttempt] = useState(0);

  const target = resolveStorybookTarget(params.return, window.location.origin);
  const published = isStorybookPublished(environment.key);

  useEffect(() => {
    if (!published) {
      return;
    }

    let cancelled = false;

    void openStorybookSession().then((outcome) => {
      if (cancelled) {
        return;
      }
      if (outcome.kind === "opened") {
        // `replace` keeps this screen out of history, so going back from
        // Storybook returns to wherever the user came from.
        window.location.replace(target);
        return;
      }
      setResult(outcome);
    });

    return () => {
      cancelled = true;
    };
  }, [target, attempt, published]);

  const retry = () => {
    setResult(null);
    setAttempt((previous) => previous + 1);
  };

  return (
    <View flex={1} bg="background" style={{ alignItems: "center", justifyContent: "center", padding: 24 }}>
      <View
        p="3xl"
        bg="surface"
        borderWidth={1}
        borderColor="border"
        rounded={sizes.radius}
        gap="lg"
        style={{ maxWidth: 420, alignItems: "center" }}
      >
        {!published && (
          <>
            <Text textStyle="md/semibold">Storybook is not published here</Text>
            <Text textStyle="sm/regular" color="textMuted" style={{ textAlign: "center" }}>
              The component libraries are only deployed to the integration environment. You are on {environment.fqdn}.
            </Text>
          </>
        )}

        {published && result === null && (
          <>
            <ActivityIndicator color={theme.colors.text} />
            <Text textStyle="sm/regular" color="textMuted">
              Opening Storybook…
            </Text>
          </>
        )}

        {result?.kind === "denied" && (
          <>
            <Text textStyle="md/semibold">Storybook is not open to this account</Text>
            <Text textStyle="sm/regular" color="textMuted" style={{ textAlign: "center" }}>
              It needs a @{STORYBOOK_EMAIL_DOMAIN} account with the {FEATURE_FLAGS.STORYBOOK} flag switched on.
            </Text>
          </>
        )}

        {result?.kind === "failed" && (
          <>
            <Text textStyle="md/semibold">Could not open Storybook</Text>
            <Text textStyle="sm/regular" color="textMuted" style={{ textAlign: "center" }}>
              {result.detail}
            </Text>
            <Button label="Try again" variant="primary" size="sm" onPress={retry} />
          </>
        )}
      </View>
    </View>
  );
}
